import { useMemo } from 'react';
import type { CanvasItem } from '../types';
import { isTableItem } from '../types';
import { UnseatedPanel } from '../../seating/UnseatedPanel';
import { PartySheet } from '../../seating/PartySheet';
import { SeatingNoticeBar, UndoRedoButtons } from '../../seating/SeatingNotice';
import { seatedTotals, unseatedParties } from '../../seating/boardModel';
import type { FloorPlanSeatingMode } from './useFloorPlanSeatingMode';

const NO_PROPOSAL = new Set<string>();

/**
 * What stands in for the object library in Seating mode: who is still to
 * seat (dragged onto a table or chair, or tapped then a table or chair
 * tapped), undo and redo, and the sheet of a seated party tapped on the plan.
 */
export function SeatingModePanel({
  mode,
  items,
}: {
  mode: FloorPlanSeatingMode;
  /** The canvas as drawn — a table added since the last save cannot take guests yet. */
  items: CanvasItem[];
}) {
  const { state, actions } = mode;
  const unsaved = useMemo(() => {
    if (!state) return 0;
    const known = new Set(state.tables.map((t) => t.uid));
    return items.filter((item) => isTableItem(item) && item.seats > 0 && !known.has(item.uid))
      .length;
  }, [state, items]);

  if (!state) {
    return (
      <div
        className="h-40 animate-pulse rounded-lg bg-gray-100"
        data-testid="seating-mode-loading"
      />
    );
  }

  const { seated, expected } = seatedTotals(state);
  const selected = mode.selected ? actions.parties.get(mode.selected) : undefined;
  const sheetParty = mode.sheetGuest ? (actions.parties.get(mode.sheetGuest) ?? null) : null;
  const closeSheet = () => mode.setSheetGuest(null);

  return (
    <div className="flex flex-col gap-2" data-testid="seating-mode-panel">
      <div className="flex items-center gap-1">
        <span className="flex-1 text-sm text-gray-700" data-testid="seating-mode-summary">
          <strong>{seated}</strong>/{expected} seated
        </span>
        <UndoRedoButtons actions={actions} />
      </div>

      {state.plan.seatingLockedAt && (
        <p className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          Seating is locked for printing. The office can still change it; clients cannot.
        </p>
      )}
      {unsaved > 0 && (
        <p
          className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800"
          data-testid="seating-mode-unsaved"
        >
          Save the plan to seat guests at {unsaved === 1 ? 'the table' : `the ${unsaved} tables`}{' '}
          added since.
        </p>
      )}
      {selected ? (
        <p className="rounded-md bg-primary-50 px-2 py-1.5 text-xs text-primary-800" role="status">
          <strong>{selected.name}</strong> picked up — tap a table or a chair on the plan, or Escape
          to put them down.
        </p>
      ) : (
        <p className="text-xs text-gray-500">
          Drag a party onto a table or a chair, or tap it and then tap where it goes. Tap a taken
          chair to move or swap its party.
        </p>
      )}

      <UnseatedPanel
        parties={unseatedParties(state, mode.filter)}
        filter={mode.filter}
        onFilterChange={mode.setFilter}
        selectedGuestId={mode.selected}
        proposed={NO_PROPOSAL}
        canWrite
        onSelect={mode.toggleSelected}
      />

      <PartySheet
        party={sheetParty}
        tables={state.tables}
        parties={actions.parties}
        canWrite
        busy={actions.busy}
        onClose={closeSheet}
        onMove={(tableUid) => {
          const guestId = mode.sheetGuest;
          closeSheet();
          if (guestId) void mode.seat(guestId, tableUid);
        }}
        onSwap={(other) => {
          const guestId = mode.sheetGuest;
          closeSheet();
          if (guestId) void actions.swap(guestId, other);
        }}
        onPin={(value) => {
          const guestId = mode.sheetGuest;
          closeSheet();
          if (guestId) void actions.pin(guestId, value);
        }}
        onMoveMember={(memberId, tableUid) => {
          if (mode.sheetGuest) void actions.seatMember(mode.sheetGuest, memberId, tableUid);
        }}
      />
      <SeatingNoticeBar actions={actions} />
    </div>
  );
}
